import { Recurrence, StaticInitializers } from '@tableau/self-service-schedules';
import {
  SelfServiceSchedulePane,
  SelfServiceSchedulePaneType,
} from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, DialogFooterButtonsWidget, DropdownButton, TabStyles, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React, { useEffect, useState } from 'react';

import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { cssClass } from '~src/style';

import { Messages } from '../../codegen/Localize';
import { areRecurrencesIdentical } from '../utils/FlowSelfServiceSchedulesUtils';

const cssPrefix = 'FlowTaskSelfServiceSchedulePickerPopup';

const dialogTitle = cssClass(cssPrefix, 'dialogHeader', {
  backgroundColor: TabStyles.Colors.F2,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: TabStyles.Sizing.BaseUnit * 5,
});

const titleContent = cssClass(cssPrefix, 'titleContent', {
  color: TabStyles.Typography.Color.Primary,
  margin: `0 ${TabStyles.Spacing.LargeSpace}px`,
});

const dialogContentMargin = cssClass(cssPrefix, 'dialogContentMargin', {
  margin: `${TabStyles.Spacing.LargeSpace}px`,
});

const selfServiceSchedulePaneWrapper = cssClass(cssPrefix, 'selfServiceSchedulePaneWrapper', {
    margin: `${TabStyles.Spacing.LargeSpace}px 0`
  });

export type FlowTaskSelfServiceSchedulePickerPopupProps = {
  onScheduleSave: (frequency: Recurrence) => void;
  scheduledTaskFrequency: Recurrence;
  dropdownButtonRef: React.MutableRefObject<DropdownButton>;
};

export function FlowTaskSelfServiceSchedulePickerPopup(props: FlowTaskSelfServiceSchedulePickerPopupProps): JSX.Element {

  const [tempRecurrence, setTempRecurrence] = useState(props.scheduledTaskFrequency || StaticInitializers.defaultRecurrence());
  const [timeZoneDetails, setTimeZoneDetails] = useState(null);

  useEffect(() => {
    const server = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: server.sessionSiteUrlName(),
      queryString: window.location.search,
    }).userSettingsUrl(
      server.sessionUserInfo().username,
      server.sessionUserInfo().domainName
    );
    setTimeZoneDetails({
      timeZonesEnabled: false,
      timeZoneDisplayName: server.userTimeZone().displayName.replace('_', ' '),
      linkToUserSettings: userAccountSettingsLink,
    });
  },[]);

  const footerButtons = () => {
    return (
      <DialogFooterButtonsWidget
        buttons={[{
          handleClick: () => {
            props.dropdownButtonRef?.current?.toggle();
          },
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: `${cssPrefix}-cancel`
        }, {
          handleClick: () => {
            props.onScheduleSave(tempRecurrence);
            props.dropdownButtonRef?.current?.toggle();
          },
          buttonType: ButtonType.Primary,
          children: Messages.save(),
          disabled: areRecurrencesIdentical(props.scheduledTaskFrequency, tempRecurrence),
          testId: `${cssPrefix}-save`
        }]}
        testId={'schedule-flow-run-footer'}
      />
    );
  };

  return (
    <>
      <div className={dialogTitle}>
        <div className={titleContent}>
          {Messages.frequency()}: {IScheduleInfo.getRecurrenceDescription(tempRecurrence)}
        </div>
        <div className={titleContent}>
          {`${Messages.timeZone()}: `}
          {timeZoneDetails &&
            <TextLinkWidget
                href={timeZoneDetails.linkToUserSettings}
                kind={TextLinkKind.Standalone}
                testId={`${cssPrefix}-timeZone`}
            >
              {timeZoneDetails.timeZoneDisplayName}
            </TextLinkWidget>}
        </div>
      </div>
      <div className={dialogContentMargin}>
        <div className={selfServiceSchedulePaneWrapper}>
            <SelfServiceSchedulePane
                recurrenceModel={tempRecurrence}
                recurrenceModelChangedCallback={(recurrence) =>
                    setTempRecurrence(recurrence)
                }
                timeZoneDetails={timeZoneDetails}
                type={SelfServiceSchedulePaneType.FlowRunTask}
                uncollapsible={true}
            />
        </div>
        {footerButtons()}
      </div>
    </>
  );
}
